import { api } from 'app/api';
import { IUpdateTDPVersionSections } from 'modules/tdp/types';
import { IThreadMinimized } from 'modules/threads/types';
import { IPaginatedResponse } from 'types';
import { IUserMinimize } from 'types/user';

export interface TDP {
  id: number;
  name: string;
  primaryThread: IThreadMinimized;
  createdAt: string;
  updatedAt: string;
  template: TDPTemplate;
  relatedThreadIds: number[];
}

export enum TDPVersionStatus {
  NotStarted = 'notstarted',
  Generated = 'generated',
  Generating = 'generating',
  UserReviewed = 'userreviewed',
  Error = 'error',
  Locked = 'locked',
}

export interface TDPVersionSection {
  id: number;
  name: string;
  content: string;
  sectionNumber: number;
}

export interface TDPVersion {
  id: number;
  versionNumber: number;
  creator: IUserMinimize;
  status: TDPStatus;
  createdAt: string;
  updatedAt: string;
  sections: Record<TDPVersionStatus, TDPVersionSection[]>;
}

enum TDPStatus {
  InProgress = 'IN_PROGRESS',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
}

export interface TDPTemplate {
  id: number;
  name: string;
  sections: TDPTemplateSection[];
  creator: IUserMinimize;
  createdAt: string;
  updatedAt: string;
}

export interface TDPTemplateSection {
  id: number;
  sectionNumber: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface ITDPsParams {
  limit: number;
  offset: number;
  primaryThread: number | string;
}

export const tdpApi = api.injectEndpoints({
  endpoints: (build) => ({
    TDPs: build.query<IPaginatedResponse<TDP>, Partial<ITDPsParams>>({
      query: (args) => ({
        url: '/tdps/',
        params: { ...args },
      }),
      providesTags: ['tdps'],
    }),
    TDP: build.query<TDP & { versions: TDPVersion[] }, { id: string }>({
      query: ({ id }) => ({
        url: `/tdps/${id}/`,
      }),
      providesTags: ['tdps'],
    }),
    createTDP: build.mutation<
      { id: number },
      { name: string; primaryThread: number; template: number; relatedThreadIds?: string[] }
    >({
      query: (body) => ({
        url: '/tdps/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tdps'],
    }),
    createTDPVersion: build.mutation<TDPVersion, { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/tdps/${id}/versions/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['tdps'],
    }),
    updateVersionSections: build.mutation<unknown, IUpdateTDPVersionSections>({
      query: ({ id, versionSectionId, action }) => ({
        url: `/tdps/${id}/version_sections/`,
        method: 'PATCH',
        body: {
          versionSectionId,
          action,
        },
      }),
      invalidatesTags: ['tdps'],
    }),
  }),
});

export const {
  useTDPQuery,
  useTDPsQuery,
  useCreateTDPMutation,
  useCreateTDPVersionMutation,
  useUpdateVersionSectionsMutation,
} = tdpApi;
